import React, { useState, useEffect } from "react";
import { Button, Dropdown, Tab, Modal, Form, Image } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL } from "../../../config";
import {
  setCourses,
  setJobCategory,
} from "../../../store/actions/CourseAction";
import CompanyCard from "./CompanyCard";

const CompanyList = () => {
  const dispatch = useDispatch();
  const collegeId = useSelector((state) => state.college_id.college_id);
  const courses = useSelector((state) => state.courses.courses);

  // States for filters
  const [category, setCategory] = useState("All");
  const [dateFilter, setDateFilter] = useState("Till now");
  const [selectedCourse, setSelectedCourse] = useState("All Courses");
  const [searchQuery, setSearchQuery] = useState("");
  const [companyList, setCompanyList] = useState([]);
  const [companyMsg, setCompanyMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileImageError, setProfileImageError] = useState({});

  // Add a refresh trigger counter
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecord, setTotalRecord] = useState(0);
  const [companiesPerPage] = useState(10);

  // Modal states
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [requestMessage, setRequestMessage] = useState("");
  const categoryOptions = useSelector(
    (state) => state.job_category.job_category,
  );

  useEffect(() => {
    const storedCourses = localStorage.getItem("courses");
    if (storedCourses) {
      dispatch(setCourses(JSON.parse(storedCourses)));
    }
    const storedCategory = localStorage.getItem("job_category");
    if (storedCategory) {
      dispatch(setJobCategory(JSON.parse(storedCategory)));
    }
  }, [dispatch]);

  // Handle image error
  const handleImageError = (companyId) => {
    setProfileImageError((prev) => ({
      ...prev,
      [companyId]: true,
    }));
  };

  const dateFilterOptions = [
    "Till now",
    "Last Month",
    "Last 3 Months",
    "Last 6 Months",
    "Last Year",
  ];

  const totalPages = Math.ceil(totalRecord / companiesPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 0 && pageNumber < totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Submit campus drive request
  const submitRequest = async () => {
    // Validate request message
    if (!requestMessage.trim()) {
      toast.error("Please enter a message for your campus drive request");
      return;
    }

    try {
      setLoading(true);
      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      const authToken = userDetails?.data?.auth_token;

      const formData = new FormData();
      formData.append("college_id", collegeId);
      formData.append("company_id", selectedCompany.company_id);
      formData.append("message", requestMessage);

      // This is a mock API call since the actual endpoint isn't provided
      // Replace with actual API endpoint when available
      const response = await axios.post(
        `${API_URL}/api/freshment_portal_apis/CompanyConnect/request_campus_drive`,
        formData,
        {
          headers: {
            Authorization: authToken,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data && response.data.success) {
        setShowRequestModal(false);
        setRequestMessage("");
        toast.success("Campus drive request sent successfully");
      } else {
        throw new Error(response.data?.message || "Failed to send request");
      }
    } catch (error) {
      console.error("Error sending campus drive request:", error);
      toast.error(
        error.message || "Failed to send request. Please try again later.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Render pagination
  const renderPagination = () => {
    const maxPagesToShow = 5; // Show max 5 page numbers at a time
    let startPage = Math.max(0, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(0, endPage - maxPagesToShow + 1);
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-4 mb-4">
        <div className="dataTables_info">
          Showing {currentPage * companiesPerPage + 1} to{" "}
          {Math.min((currentPage + 1) * companiesPerPage, totalRecord)} of{" "}
          {totalRecord} entries
        </div>
        <div className="d-flex align-items-center">
          <nav>
            <ul className="pagination pagination-gutter pagination-primary no-bg">
              {/* First Page */}
              <li
                className={`page-item ${currentPage === 0 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(0)}
                  disabled={currentPage === 0}
                >
                  <i className="fa fa-angle-double-left"></i>
                </button>
              </li>

              {/* Previous */}
              <li
                className={`page-item ${currentPage === 0 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                >
                  <i className="fa fa-angle-left"></i>
                </button>
              </li>

              {/* Show ellipsis if not at start */}
              {startPage > 0 && (
                <li className="page-item">
                  <span className="page-link">...</span>
                </li>
              )}

              {/* Page Numbers */}
              {Array.from(
                { length: endPage - startPage + 1 },
                (_, i) => startPage + i,
              ).map((pageNum) => (
                <li
                  key={pageNum}
                  className={`page-item ${
                    pageNum === currentPage ? "active" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum + 1}
                  </button>
                </li>
              ))}

              {/* Show ellipsis if not at end */}
              {endPage < totalPages - 1 && (
                <li className="page-item">
                  <span className="page-link">...</span>
                </li>
              )}

              {/* Next */}
              <li
                className={`page-item ${
                  currentPage === totalPages - 1 ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages - 1}
                >
                  <i className="fa fa-angle-right"></i>
                </button>
              </li>

              {/* Last Page */}
              <li
                className={`page-item ${
                  currentPage === totalPages - 1 ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(totalPages - 1)}
                  disabled={currentPage === totalPages - 1}
                >
                  <i className="fa fa-angle-double-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    );
  };

  const resetFilter = () => {
    setCategory("All");
    setDateFilter("Till now");
    setSelectedCourse("All Courses");
    setSearchQuery("");
    setCurrentPage(0);
    setRefreshTrigger((prev) => prev + 1);
  };

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      const authToken = userDetails?.data?.auth_token;

      const formData = new FormData();
      formData.append("limit", companiesPerPage);
      formData.append("offset", currentPage * companiesPerPage);
      formData.append("college_id", collegeId);
      if (searchQuery.trim() !== "") {
        formData.append("search", searchQuery);
      }

      if (dateFilter !== "Till now") {
        switch (dateFilter) {
          case "Last Month":
            formData.append("filter_by_date", "lastmonth");
            break;
          case "Last 3 Months":
            formData.append("filter_by_date", "last3months");
            break;
          case "Last 6 Months":
            formData.append("filter_by_date", "last6months");
            break;
          case "Last Year":
            formData.append("filter_by_date", "last1year");
            break;
          default:
            formData.append("filter_by_date", null);
        }
      }

      if (category !== "All") {
        formData.append("filter_by_category", category);
      }

      if (selectedCourse !== "All Courses") {
        formData.append("filter_by_course", selectedCourse);
      }

      const response = await axios.post(
        `${API_URL}/api/freshment_portal_apis/CompanyConnect/get_listed_company_list`,
        formData,
        {
          headers: {
            Authorization: authToken,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        console.log(response.data.data);
        setCompanyList(response.data.data);
        setTotalRecord(response.data.totalRecords || response.data.data.length);
      } else {
        setCompanyList([]);
        setTotalRecord(0);
        setCompanyMsg(response.data.message || "No companies found");
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompanyMsg("Error fetching companies. Please try again later.");
      setCompanyList([]);
      setTotalRecord(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchCompanies();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [category, dateFilter, selectedCourse, currentPage, refreshTrigger]);

  const handleSearch = () => {
    setCurrentPage(0);
    setRefreshTrigger((prev) => prev + 1);
  };

  return (
    <>
      {/* Filter Section */}
      <div className="d-flex align-items-center flex-wrap search-job bg-white px-0 mb-4">
        <div className="col-xl-2 col-xxl-2 col-lg-2 col-sm-4 col-12 search-dropdown d-flex align-items-center">
          <Dropdown className="form-control border-0 style-1 h-auto">
            <Dropdown.Toggle
              as="div"
              className="fs-16 font-w500 text-primary d-flex align-items-center i-false"
            >
              {dateFilter}
              <i className="fas fa-angle-down text-primary scale5 ms-3"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu dropdown-menu-end mt-1">
              {dateFilterOptions.map((filter) => (
                <Dropdown.Item
                  key={filter}
                  onClick={() => setDateFilter(filter)}
                  className="text-primary"
                >
                  {filter}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        {/* Course Filter */}
        <div className="col-xl-2 col-xxl-2 col-lg-2 col-sm-4 col-12 search-dropdown d-flex align-items-center">
          <Dropdown className="form-control border-0 style-1 h-auto">
            <Dropdown.Toggle
              as="div"
              className="fs-16 font-w500 text-primary d-flex align-items-center i-false"
            >
              {courses.find((course) => course.filter_id === selectedCourse)
                ?.name ?? "All Courses"}
              <i className="fas fa-angle-down text-primary scale5 ms-3"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu dropdown-menu-end mt-1">
              <Dropdown.Item
                onClick={() => setSelectedCourse("All Courses")}
                className="text-primary"
              >
                All Courses
              </Dropdown.Item>
              {courses.map((course, index) => (
                <Dropdown.Item
                  key={index}
                  onClick={() => setSelectedCourse(course.filter_id)}
                  className="text-primary"
                >
                  {course.name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        {/* Search Box */}
        <div className="col-xl-7 col-xxl-7 col-lg-7 col-12 d-md-flex job-title-search pe-0">
          <div className="input-group search-area">
            <input
              type="text"
              value={searchQuery}
              className="form-control h-auto"
              placeholder="Search company by name or email"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="input-group-text">
              <Button onClick={handleSearch} className="btn btn-primary btn-sm">
                Search<i className="flaticon-381-search-2 ms-1" style={{ fontSize: '14px', verticalAlign: 'middle' }}></i>
              </Button>
            </span>
          </div>
        </div>{" "}
        <div className="col-xl-1 col-xxl-1 col-lg-1 col-12 d-flex align-items-center justify-content-lg-end justify-content-center">
          <button
            onClick={async () => {
              resetFilter();
            }}
            className="btn btn-danger btn-sm"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="mb-4">
        <div
          className="d-flex align-items-center"
          style={{
            backgroundColor: "white",
            borderRadius: "10px",
            width: "fit-content",
          }}
        >
          <div className="btn-group" role="group" style={{ gap: 3 }}>
            <button
              className="btn btn-md"
              style={
                category === "All"
                  ? {
                      backgroundColor: "#ffa755",
                      borderRadius: "10px",
                      color: "white",
                      fontWeight: "bold",
                    }
                  : { backgroundColor: "white", borderRadius: "10px" }
              }
              onClick={() => setCategory("All")}
            >
              All
            </button>
            {categoryOptions &&
              categoryOptions.length > 0 &&
              categoryOptions.map((cat, index) => (
                <button
                  key={index}
                  className="btn btn-md"
                  style={
                    cat.filter_id === category
                      ? {
                          backgroundColor: "#ffa755",
                          borderRadius: "10px",
                          color: "white",
                          fontWeight: "bold",
                        }
                      : { backgroundColor: "white", borderRadius: "10px" }
                  }
                  onClick={() => setCategory(cat.filter_id)}
                >
                  {cat.category_name}
                </button>
              ))}
          </div>
        </div>
      </div>

      {/* Company List */}
      <div className="row">
        <div className="col-12">
          {loading ? (
            <div className="text-center my-4">
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : companyList && companyList.length > 0 ? (
            <Tab.Container defaultActiveKey="Boxed">
              <div className="mb-4">
                <h4 className="mb-0">
                  Showing {currentPage * companiesPerPage + 1} to{" "}
                  {Math.min((currentPage + 1) * companiesPerPage, totalRecord)}{" "}
                  of {totalRecord} entries
                </h4>
              </div>
              <div className="d-flex justify-content-between align-items-center flex-wrap w-100">
                <div className="d-flex align-items-center mb-4 w-100">
                  <Tab.Content className="w-100">
                    <Tab.Pane eventKey="Boxed">
                      <div className="row">
                        {companyList.map((company, index) => (
                          <CompanyCard
                            key={index}
                            company={company}
                            profileImageError={profileImageError}
                            handleImageError={handleImageError}
                          />
                        ))}
                      </div>
                      {renderPagination()}
                    </Tab.Pane>
                  </Tab.Content>
                </div>
              </div>
            </Tab.Container>
          ) : (
            <div className="text-center py-4">
              <h4>{companyMsg || "No companies found"}</h4>
            </div>
          )}
        </div>
      </div>

      {/* Request Campus Drive Modal */}
      <Modal
        show={showRequestModal}
        onHide={() => setShowRequestModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Request Campus Drive</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCompany && (
            <div className="mb-3">
              <h5>Company: {selectedCompany.company_name}</h5>
              <p>Email: {selectedCompany.company_email}</p>
            </div>
          )}
          <Form.Group>
            <Form.Label>Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={requestMessage}
              onChange={(e) => setRequestMessage(e.target.value)}
              placeholder="Please provide details about your campus drive request..."
              style={{ height: "100px", border: "1px solid #cccccc" }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger light"
            onClick={() => setShowRequestModal(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={submitRequest}>
            Send Request
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CompanyList;
